import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

const EXPERIENCE_LEVELS = [
  { value: 'None', key: 'wizard.experience.levels.none' },
  { value: 'Less than 1 year', key: 'wizard.experience.levels.lt1' },
  { value: '1-2 years', key: 'wizard.experience.levels.1to2' },
  { value: '3-5 years', key: 'wizard.experience.levels.3to5' },
  { value: '6-10 years', key: 'wizard.experience.levels.6to10' },
  { value: '11-15 years', key: 'wizard.experience.levels.11to15' },
  { value: '16-20 years', key: 'wizard.experience.levels.16to20' },
  { value: 'More than 20 years', key: 'wizard.experience.levels.gt20' }
];

const INDUSTRIES = [
  { value: 'Information Technology', key: 'wizard.experience.industries.it' },
  { value: 'Healthcare', key: 'wizard.experience.industries.healthcare' },
  { value: 'Engineering', key: 'wizard.experience.industries.engineering' },
  { value: 'Finance & Banking', key: 'wizard.experience.industries.finance' },
  { value: 'Education', key: 'wizard.experience.industries.education' },
  { value: 'Sales & Marketing', key: 'wizard.experience.industries.salesMarketing' },
  { value: 'Manufacturing', key: 'wizard.experience.industries.manufacturing' },
  { value: 'Construction', key: 'wizard.experience.industries.construction' },
  { value: 'Hospitality', key: 'wizard.experience.industries.hospitality' },
  { value: 'Transportation', key: 'wizard.experience.industries.transportation' },
  { value: 'Agriculture', key: 'wizard.experience.industries.agriculture' },
  { value: 'Government/Public Sector', key: 'wizard.experience.industries.government' },
  { value: 'Consulting', key: 'wizard.experience.industries.consulting' },
  { value: 'Real Estate', key: 'wizard.experience.industries.realEstate' },
  { value: 'Media & Communications', key: 'wizard.experience.industries.media' },
  { value: 'Other', key: 'wizard.experience.industries.other' }
];

const JOB_STATUSES = [
  { value: 'Currently Employed', key: 'wizard.experience.jobStatus.employed' },
  { value: 'Self-Employed', key: 'wizard.experience.jobStatus.selfEmployed' },
  { value: 'Unemployed', key: 'wizard.experience.jobStatus.unemployed' },
  { value: 'Student', key: 'wizard.experience.jobStatus.student' },
  { value: 'Freelancer', key: 'wizard.experience.jobStatus.freelancer' },
  { value: 'Business Owner', key: 'wizard.experience.jobStatus.businessOwner' },
  { value: 'Retired', key: 'wizard.experience.jobStatus.retired' }
];

/**
 * Experience component for collecting user's work experience
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const Experience = ({
  stepNumber = 5,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = '',
  description = '',
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const defaultTitle = t('wizard.experience.title', 'Work Experience');
  const defaultDescription = t('wizard.experience.description', 'Your professional experience can significantly impact your immigration eligibility and points calculation.');
  const stepTitle = title || defaultTitle;
  const stepDescription = description || defaultDescription;
  const [formData, setFormData] = useState({
    totalExperience: state.experience.totalExperience || '',
    hasInternationalExperience: state.experience.hasInternationalExperience || '',
    jobTitle: state.experience.jobTitle || '',
    company: state.experience.company || '',
    industry: state.experience.industry || '',
    skills: state.experience.skills || '',
    currentJobStatus: state.experience.currentJobStatus || ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.totalExperience) {
      newErrors.totalExperience = t('wizard.experience.errors.totalExperience.required', 'Please select your total work experience');
    }

    // Only validate other fields if total experience is not "None"
    if (formData.totalExperience !== 'None') {
      if (!formData.hasInternationalExperience) {
        newErrors.hasInternationalExperience = t('wizard.experience.errors.hasInternationalExperience.required', 'Please select your international experience status');
      }

      if (!formData.jobTitle.trim()) {
        newErrors.jobTitle = t('wizard.experience.errors.jobTitle.required', 'Job title is required');
      }

      if (!formData.company.trim()) {
        newErrors.company = t('wizard.experience.errors.company.required', 'Company name is required');
      }

      if (!formData.industry) {
        newErrors.industry = t('wizard.experience.errors.industry.required', 'Please select your industry');
      }

      if (!formData.currentJobStatus) {
        newErrors.currentJobStatus = t('wizard.experience.errors.currentJobStatus.required', 'Please select your current job status');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handleNext = useCallback(() => {
    if (validateForm()) {
      // Update wizard state with experience info
      actions.updateExperience([formData]);

      if (onNext) {
        onNext({
          experience: formData
        });
      }
    }
  }, [formData, validateForm, actions, onNext]);

  const canProceed = useMemo(() => {
    const requiredFields = ['totalExperience'];
    
    // Only require other fields if total experience is not "None"
    if (formData.totalExperience !== 'None') {
      requiredFields.push('hasInternationalExperience', 'jobTitle', 'company', 'industry', 'currentJobStatus');
    }
    
    return requiredFields.every(field => {
      if (field === 'jobTitle' || field === 'company') {
        return formData[field] && formData[field].trim();
      }
      return formData[field];
    }) && Object.keys(errors).every(field => !errors[field]);
  }, [formData, errors]);

  // Validate on mount to show any initial errors
  React.useEffect(() => {
    if (Object.keys(formData).some(key => formData[key])) {
      validateForm();
    }
  }, []);

  return (
    <div className={`experience-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Form */}
      <div className="card p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Experience */}
          <div>
            <label htmlFor="totalExperience" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.totalExperience.label', 'Total Work Experience *')}
            </label>
            <select
              id="totalExperience"
              value={formData.totalExperience}
              onChange={(e) => handleInputChange('totalExperience', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.totalExperience ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{t('wizard.experience.fields.totalExperience.placeholder', 'Select your experience')}</option>
              {EXPERIENCE_LEVELS.map(level => (
                <option key={level.value} value={level.value}>{t(level.key, level.value)}</option>
              ))}
            </select>
            {errors.totalExperience && (
              <p className="mt-1 text-sm text-red-600">{errors.totalExperience}</p>
            )}
          </div>

          {/* International Experience */}
          <div className={formData.totalExperience === 'None' ? 'opacity-50 pointer-events-none' : ''}>
            <label htmlFor="hasInternationalExperience" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.hasInternationalExperience.label', 'International Work Experience *')}
            </label>
            <select
              id="hasInternationalExperience"
              value={formData.hasInternationalExperience}
              onChange={(e) => handleInputChange('hasInternationalExperience', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.hasInternationalExperience ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={formData.totalExperience === 'None'}
            >
              <option value="">{t('wizard.experience.fields.hasInternationalExperience.placeholder', 'Select international experience')}</option>
              <option value="yes">{t('wizard.experience.fields.hasInternationalExperience.options.yes', 'Yes, I have international work experience')}</option>
              <option value="no">{t('wizard.experience.fields.hasInternationalExperience.options.no', 'No, I only worked in my home country')}</option>
            </select>
            {errors.hasInternationalExperience && (
              <p className="mt-1 text-sm text-red-600">{errors.hasInternationalExperience}</p>
            )}
          </div>

          {/* Current/Most Recent Job Title */}
          <div className={formData.totalExperience === 'None' ? 'opacity-50 pointer-events-none' : ''}>
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.jobTitle.label', 'Current/Most Recent Job Title *')}
            </label>
            <input
              type="text"
              id="jobTitle"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.jobTitle ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('wizard.experience.fields.jobTitle.placeholder', 'e.g., Software Engineer, Marketing Manager')}
              disabled={formData.totalExperience === 'None'}
            />
            {errors.jobTitle && (
              <p className="mt-1 text-sm text-red-600">{errors.jobTitle}</p>
            )}
          </div>

          {/* Company */}
          <div className={formData.totalExperience === 'None' ? 'opacity-50 pointer-events-none' : ''}>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.company.label', 'Current/Most Recent Company *')}
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.company ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('wizard.experience.fields.company.placeholder', 'Enter your company name')}
              disabled={formData.totalExperience === 'None'}
            />
            {errors.company && (
              <p className="mt-1 text-sm text-red-600">{errors.company}</p>
            )}
          </div>

          {/* Industry */}
          <div className={formData.totalExperience === 'None' ? 'opacity-50 pointer-events-none' : ''}>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.industry.label', 'Industry *')}
            </label>
            <select
              id="industry"
              value={formData.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.industry ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={formData.totalExperience === 'None'}
            >
              <option value="">{t('wizard.experience.fields.industry.placeholder', 'Select your industry')}</option>
              {INDUSTRIES.map(industry => (
                <option key={industry.value} value={industry.value}>{t(industry.key, industry.value)}</option>
              ))}
            </select>
            {errors.industry && (
              <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
            )}
          </div>

          {/* Current Job Status */}
          <div className={formData.totalExperience === 'None' ? 'opacity-50 pointer-events-none' : ''}>
            <label htmlFor="currentJobStatus" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.currentJobStatus.label', 'Current Employment Status *')}
            </label>
            <select
              id="currentJobStatus"
              value={formData.currentJobStatus}
              onChange={(e) => handleInputChange('currentJobStatus', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.currentJobStatus ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={formData.totalExperience === 'None'}
            >
              <option value="">{t('wizard.experience.fields.currentJobStatus.placeholder', 'Select your status')}</option>
              {JOB_STATUSES.map(status => (
                <option key={status.value} value={status.value}>{t(status.key, status.value)}</option>
              ))}
            </select>
            {errors.currentJobStatus && (
              <p className="mt-1 text-sm text-red-600">{errors.currentJobStatus}</p>
            )}
          </div>

          {/* Skills */}
          <div className="md:col-span-2">
            <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.experience.fields.skills.label', 'Key Skills & Expertise (Optional)')}
            </label>
            <textarea
              id="skills"
              value={formData.skills}
              onChange={(e) => handleInputChange('skills', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder={t('wizard.experience.fields.skills.placeholder', 'List your key professional skills, technical expertise, and specializations')}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('wizard.experience.fields.skills.helper', 'Include technical skills, software proficiency, languages, and professional certifications')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.next', 'Next Step')}
        </button>
      </div>
    </div>
  );
};

export default Experience;
